import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, HostBinding, NgModule } from '@angular/core';
import { BcInlineAlertBehavior } from '@bancolombia/design-system-behaviors';
import { BcDialog } from '@bancolombia/design-system-web/bc-services';
import * as i1 from '@bancolombia/design-system-web/bc-helpers';
import * as i2 from '@angular/router';
import { RouterLink } from '@angular/router';
import * as i3 from '@bancolombia/design-system-web/bc-link';
import { BcLinkModule } from '@bancolombia/design-system-web/bc-link';
import * as i4 from '@bancolombia/design-system-web/bc-icon-button';
import { BcIconButtonModule } from '@bancolombia/design-system-web/bc-icon-button';
import * as i5 from '@bancolombia/design-system-web/bc-transaction-status';
import { BcTransactionStatusModule } from '@bancolombia/design-system-web/bc-transaction-status';
import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Importa las alertas con los diseños de Bancolombia S.A.
 * Las alertas corresponde a un cuadro de diálogo que disponibiliza
 * información de manera flotante o fija.
 *
 * @export
 * @class BcInlineAlertComponent
 * @implements {AfterViewInit}
 */
class BcInlineAlertComponent extends BcDialog {
    get sizeSmall() { return !this.isSizeLarge; }
    ;
    get sizeLarge() { return this.isSizeLarge; }
    ;
    constructor(helper, _router) {
        super();
        this.helper = helper;
        this._router = _router;
        /**
         * Indica si el contenido de la alerta contiene un routerLink
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.contentRouterLink = false;
        /**
         * Texto del párrafo del Inline. Acepta elementos HTML para tener un estilo parametrizable.
         *
         * @type {string}
         * @memberof BcInlineAlertComponent
         */
        this.text = '';
        /**
         * Indica si el Inline es creado por medio del servicio DialogService, requerido para la emisión de eventos.
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.createdByService = false;
        /**
        * Título de la alerta.
        *
        * @type {string}
        * @memberof BcInlineAlertComponent
        */
        this.title = '';
        /**
         * Textos de los botones del Inline. Indicar nulo si el Inline no lleva botones.
         *
         * @type {([string] | [string, string])}
         * @memberof BcInlineAlertComponent
         */
        this.buttons = null;
        /**
         * Variante del primer botón del Inline.
         *
         * @type {('secondary' | 'background')}
         */
        /**
        * Links en la alerta, solo se pintan los 2 primeros items del arreglo.
        * @type: Array<Link>
        * @memberof BcInlineAlertComponent
        */
        /**
        * Version lts de alerta [ "Botones" | "Links"].
        * @type InlineVersion
        * @memberof BcInlineAlertComponent
        */
        this.version = "Botones";
        this.links = [];
        /**
        * Se ejecuta cuando se le da click a un link dentro del inline.
        *
        * @type {EventEmitter<any>}
        * @memberof BcInlineAlertComponent
        */
        this.linkClick = new EventEmitter();
        this.mainButtonVariant = 'secondary';
        /**
       * Tipo de la alerta.
       *
       * @type InlineAlertType
       * @memberof BcInlineAlertComponent
       */
        this.type = 'info';
        /**
         * Id del componente.
         *
         * @type {string}
         * @memberof BcInlineAlertComponent
         */
        this.componentId = this.helper.getId(this);
        /**
         * Aria Label del botón de cierre.
         *
         * @type {string}
         * @memberof BcInlineAlertComponent
         */
        this.lectorBtnClose = "Cerrar alerta";
        /**
         * Aria Label del componente Inline Alert.
         *
         * @type {string}
         * @memberof BcInlineAlertComponent
         */
        this.lectorAlert = "Alerta preventiva";
        /**
         * Indica si el Inline lleva o no Transaction Icon Status, por defecto es verdadero.
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.hasTransactionStatus = true;
        /**
         * Si la alerta es estática, indica si se puede descartar o cerrar.
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.dismissible = false;
        /**
         * Indica si la alerta va a utilizar la variante "full-width", si no, usará la variante "in-viewport" (por defecto).
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.isFullWidth = false;
        /**
         * Indica si la alerta va a utilizar el tamaño "large", si no, usará la variante "small" (por defecto).
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.isSizeLarge = false;
        /**
         * Indica si el contenido se va a alinear al centro. Usar cuando se tenga poco texto y no se tengan botones.
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.alignCenter = false;
        /**
         * Se ejecuta cuando la alerta se cierra, aplica solo para alertas inline.
         *
         * @type {EventEmitter<any>}
         * @memberof BcInlineAlertComponent
         */
        this.onClose = new EventEmitter();
        /**
         * Se ejecuta cuando se da click a uno de los botones del inline.
         *
         * @type {EventEmitter<0 | 1>}
         * @memberof BcInlineAlertComponent
         */
        this.onButtonClick = new EventEmitter();
        /**
         * Indica el id del elemento de referencia para insertar la alerta.
         *
         * @type {EventEmitter<any>}
         * @memberof BcInlineAlertComponent
         */
        this.elementRef = '';
        this.bcAlertBehavior = new BcInlineAlertBehavior();
    }
    ngAfterViewInit() {
        this.bcAlertBehavior.setId(this.componentId);
        this.bcAlertBehavior.onClose((event) => {
            this.onClose.emit(event);
        });
        this.bcAlertBehavior.setUp();
    }
    /**
     * Procesar RouterLink del parrafo de la alerta
     *
     * @param {*} e
     * @memberof BcInlineAlertComponent
     */
    processLinks(e) {
        const element = e.target;
        if (element.nodeName === 'A') {
            e.preventDefault();
            const link = element.getAttribute('routerLink');
            this._router.navigate([link]);
        }
    }
    /**
     * Inyecta los atríbutos del componente cuando este va a ser generado por medio del DialogService.
     *
     * @param {InlineAlertInputs}
     * @memberof BcInlineAlertComponent
     */
    onInjectInputs(inputs) {
        this.elementRef = inputs.elementRef;
        this.componentId = inputs?.id || this.componentId;
        this.text = inputs?.text || this.text;
        this.title = inputs?.title || this.title;
        this.buttons = inputs?.buttons || this.buttons;
        this.links = inputs?.links || this.links;
        this.mainButtonVariant = inputs?.mainButtonVariant || this.mainButtonVariant;
        this.type = inputs?.type || this.type;
        this.lectorBtnClose = inputs?.lectorBtnClose || this.lectorBtnClose;
        this.lectorAlert = inputs?.lectorAlerta || this.lectorAlert;
        this.injectBooleanInputs(inputs);
        this.createdByService = true;
    }
    /**
     * Inyecta los atríbutos booleanos del componentelos cuales requieren una validación adicional.
     *
     * @param {InlineAlertInputs}
     * @memberof BcInlineAlertComponent
     */
    injectBooleanInputs(inputs) {
        if (inputs?.buttons) {
            this.version = "Botones";
        }
        else if (inputs?.links) {
            this.version = "Links";
        }
        this.hasTransactionStatus = inputs?.hasTransactionStatus !== undefined ? inputs.hasTransactionStatus : this.hasTransactionStatus;
        this.dismissible = inputs?.dismissible !== undefined ? inputs.dismissible : this.dismissible;
        this.isFullWidth = inputs?.isFullWidth !== undefined ? inputs.isFullWidth : this.isFullWidth;
        this.isSizeLarge = inputs?.isSizeLarge !== undefined ? inputs.isSizeLarge : this.isSizeLarge;
        this.alignCenter = inputs?.alignCenter !== undefined ? inputs.alignCenter : this.alignCenter;
        this.version = inputs?.version || this.version;
    }
    /**
     * Cierra la alerta.
     *
     * @memberof BcInlineAlertComponent
     */
    closeIt() {
        this.bcAlertBehavior.close();
        if (this.createdByService) {
            this.close('closing');
        }
    }
    /**
     * Cancela la alerta.
     *
     * @memberof BcInlineAlertComponent
     */
    cancelIt() {
        this.dismiss('cancelling');
    }
    /**
  * Emite el evento cuando se le da click a un link
  *
  * @memberof BcAlertsComponent
  */
    clickEventLink(event) {
        event.preventDefault();
        event.stopPropagation();
        this.linkClick.emit({ text: event.target.innerText, path: event.target.parentElement.href });
    }
    /**
     * Emite la posición del botón al que se le da click dentro del Inline.
     *
     * @memberof BcInlineAlertComponent
     */
    clickButtonEvent(buttonIndex) {
        this.onButtonClick.emit(buttonIndex);
        if (this.createdByService) {
            this.modalInstance.result.next(buttonIndex);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertComponent, deps: [{ token: i1.BcHelperService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BcInlineAlertComponent, selector: "bc-inline-alert", inputs: { contentRouterLink: "contentRouterLink", text: "text", title: "title", buttons: "buttons", version: "version", links: "links", mainButtonVariant: "mainButtonVariant", type: "type", componentId: "componentId", lectorBtnClose: "lectorBtnClose", lectorAlert: "lectorAlert", hasTransactionStatus: "hasTransactionStatus", dismissible: "dismissible", isFullWidth: "isFullWidth", isSizeLarge: "isSizeLarge", alignCenter: "alignCenter" }, outputs: { linkClick: "linkClick", onClose: "onClose", onButtonClick: "onButtonClick" }, host: { properties: { "class.bc-inline-alert-small": "this.sizeSmall", "class.bc-inline-alert-large": "this.sizeLarge" } }, usesInheritance: true, ngImport: i0, template: "<article\n  [id]=\"componentId\"\n  [attr.aria-label]=\"lectorAlert\"\n  role=\"alertdialog\"\n  class=\"bc-inline-alert-active bc-inline-alert bc-inline-alert-{{ type }}\"\n  [ngClass]=\"{\n    'bc-inline-alert-full-width': isFullWidth,\n    'bc-inline-alert-large': isSizeLarge,\n    'bc-inline-alert-small': !isSizeLarge,\n    'bc-inline-alert-align-center': alignCenter\n  }\"\n>\n  <section *ngIf=\"hasTransactionStatus\" class=\"bc-inline-alert-status\">\n    <bc-transaction-status\n      size=\"md\"\n      [status]=\"\n        type === 'neutral' || type === 'eventual-full-contrast' || type === 'eventual-min-contrast' ? 'generic' : type\n      \"\n    />\n  </section>\n\n  <section class=\"bc-inline-alert-body\">\n    <section class=\"bc-inline-alert-content\">\n      <p *ngIf=\"title\" class=\"bc-inline-alert-title\" [innerHTML]=\"title\"></p>\n      <p *ngIf=\"!contentRouterLink\" class=\"bc-inline-alert-text\" [innerHTML]=\"text\"></p>\n      <p *ngIf=\"contentRouterLink\" class=\"bc-inline-alert-text\" [innerHTML]=\"text\" (click)=\"processLinks($event)\"></p>\n    </section>\n\n    <section *ngIf=\"buttons !== null && version === 'Botones'\" class=\"bc-inline-alert-buttons\">\n      <button\n        *ngIf=\"buttons.length > 0\"\n        (click)=\"clickButtonEvent(0)\"\n        class=\"bc-button bc-button-small bc-button-hug\"\n        [ngClass]=\"{\n          'bc-button-secondary': mainButtonVariant === 'secondary' && type !== 'eventual-full-contrast',\n          'bc-button-background': mainButtonVariant === 'background' && type !== 'eventual-full-contrast',\n          'bc-button-secondary-inverse': type === 'eventual-full-contrast',\n        }\"\n      >\n        <span>{{ buttons[0] }}</span>\n      </button>\n\n      <button\n        *ngIf=\"buttons.length > 1\"\n        (click)=\"clickButtonEvent(1)\"\n        class=\"bc-button bc-button-small bc-button-hug\"\n        [ngClass]=\"type !== 'eventual-full-contrast' ? 'bc-button-ghost' : 'bc-button-secondary-inverse'\"\n      >\n        <span>{{ buttons[1] }}</span>\n      </button>\n    </section>\n\n    <section *ngIf=\"links !== null && version === 'Links'\" class=\"bc-inline-alert-buttons\">\n      <ul>\n        <li *ngFor=\"let l of links | slice: 0:2\">\n          <a *ngIf=\"l.url\" \n            bc-link \n            class=\"bc-inline-alert-link\" \n            [href]=\"l.url\" \n            sizeLink=\"underlineLarge\"\n            target=\"_blank\" rel=\"noopener noreferrer\" typeLink=\"underline\" (click)=\"clickEventLink($event)\" role=\"link\">\n            <u>{{ l.text }}</u>\n          </a>\n          <a *ngIf=\"!l.url\" \n            bc-link [href]=\"'#'\" \n            class=\"bc-inline-alert-link\" \n            sizeLink=\"underlineLarge\" \n            target=\"_blank\"\n            rel=\"noopener noreferrer\" \n            typeLink=\"underline\" (click)=\"clickEventLink($event)\" role=\"link\">\n            <u>{{ l.text }}</u>\n          </a>\n        </li>\n    </ul>\n    </section>\n  </section>\n\n  <section *ngIf=\"dismissible\" class=\"bc-inline-alert-close\">\n    <bc-icon-button (click)=\"closeIt()\" size=\"small\" type=\"ghost\" customIcon=\"error\" ariaLabel=\"{{ lectorBtnClose }}\" />\n  </section>\n</article>\n", dependencies: [{ kind: "directive", type: i3.BcLinkDirective, selector: "[bc-link]", inputs: ["typeLink", "sizeLink", "class"] }, { kind: "component", type: i4.BcIconButtonComponent, selector: "bc-icon-button", inputs: ["type", "color", "size", "shadow", "customIcon", "statusButton", "seleted", "selected", "ariaLabel"] }, { kind: "component", type: i5.BcTransactionStatusComponent, selector: "bc-transaction-status", inputs: ["status", "size", "customIcon", "texto", "color", "colorText", "border", "textAriaLabel"] }, { kind: "directive", type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i6.SlicePipe, name: "slice" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'bc-inline-alert', template: "<article\n  [id]=\"componentId\"\n  [attr.aria-label]=\"lectorAlert\"\n  role=\"alertdialog\"\n  class=\"bc-inline-alert-active bc-inline-alert bc-inline-alert-{{ type }}\"\n  [ngClass]=\"{\n    'bc-inline-alert-full-width': isFullWidth,\n    'bc-inline-alert-large': isSizeLarge,\n    'bc-inline-alert-small': !isSizeLarge,\n    'bc-inline-alert-align-center': alignCenter\n  }\"\n>\n  <section *ngIf=\"hasTransactionStatus\" class=\"bc-inline-alert-status\">\n    <bc-transaction-status\n      size=\"md\"\n      [status]=\"\n        type === 'neutral' || type === 'eventual-full-contrast' || type === 'eventual-min-contrast' ? 'generic' : type\n      \"\n    />\n  </section>\n\n  <section class=\"bc-inline-alert-body\">\n    <section class=\"bc-inline-alert-content\">\n      <p *ngIf=\"title\" class=\"bc-inline-alert-title\" [innerHTML]=\"title\"></p>\n      <p *ngIf=\"!contentRouterLink\" class=\"bc-inline-alert-text\" [innerHTML]=\"text\"></p>\n      <p *ngIf=\"contentRouterLink\" class=\"bc-inline-alert-text\" [innerHTML]=\"text\" (click)=\"processLinks($event)\"></p>\n    </section>\n\n    <section *ngIf=\"buttons !== null && version === 'Botones'\" class=\"bc-inline-alert-buttons\">\n      <button\n        *ngIf=\"buttons.length > 0\"\n        (click)=\"clickButtonEvent(0)\"\n        class=\"bc-button bc-button-small bc-button-hug\"\n        [ngClass]=\"{\n          'bc-button-secondary': mainButtonVariant === 'secondary' && type !== 'eventual-full-contrast',\n          'bc-button-background': mainButtonVariant === 'background' && type !== 'eventual-full-contrast',\n          'bc-button-secondary-inverse': type === 'eventual-full-contrast',\n        }\"\n      >\n        <span>{{ buttons[0] }}</span>\n      </button>\n\n      <button\n        *ngIf=\"buttons.length > 1\"\n        (click)=\"clickButtonEvent(1)\"\n        class=\"bc-button bc-button-small bc-button-hug\"\n        [ngClass]=\"type !== 'eventual-full-contrast' ? 'bc-button-ghost' : 'bc-button-secondary-inverse'\"\n      >\n        <span>{{ buttons[1] }}</span>\n      </button>\n    </section>\n\n    <section *ngIf=\"links !== null && version === 'Links'\" class=\"bc-inline-alert-buttons\">\n      <ul>\n        <li *ngFor=\"let l of links | slice: 0:2\">\n          <a *ngIf=\"l.url\" \n            bc-link \n            class=\"bc-inline-alert-link\" \n            [href]=\"l.url\" \n            sizeLink=\"underlineLarge\"\n            target=\"_blank\" rel=\"noopener noreferrer\" typeLink=\"underline\" (click)=\"clickEventLink($event)\" role=\"link\">\n            <u>{{ l.text }}</u>\n          </a>\n          <a *ngIf=\"!l.url\" \n            bc-link [href]=\"'#'\" \n            class=\"bc-inline-alert-link\" \n            sizeLink=\"underlineLarge\" \n            target=\"_blank\"\n            rel=\"noopener noreferrer\" \n            typeLink=\"underline\" (click)=\"clickEventLink($event)\" role=\"link\">\n            <u>{{ l.text }}</u>\n          </a>\n        </li>\n    </ul>\n    </section>\n  </section>\n\n  <section *ngIf=\"dismissible\" class=\"bc-inline-alert-close\">\n    <bc-icon-button (click)=\"closeIt()\" size=\"small\" type=\"ghost\" customIcon=\"error\" ariaLabel=\"{{ lectorBtnClose }}\" />\n  </section>\n</article>\n" }]
        }], ctorParameters: function () { return [{ type: i1.BcHelperService }, { type: i2.Router }]; }, propDecorators: { contentRouterLink: [{
                type: Input
            }], text: [{
                type: Input
            }], title: [{
                type: Input
            }], buttons: [{
                type: Input
            }], version: [{
                type: Input
            }], links: [{
                type: Input
            }], linkClick: [{
                type: Output
            }], mainButtonVariant: [{
                type: Input
            }], type: [{
                type: Input
            }], componentId: [{
                type: Input
            }], lectorBtnClose: [{
                type: Input
            }], lectorAlert: [{
                type: Input
            }], hasTransactionStatus: [{
                type: Input
            }], dismissible: [{
                type: Input
            }], isFullWidth: [{
                type: Input
            }], isSizeLarge: [{
                type: Input
            }], alignCenter: [{
                type: Input
            }], onClose: [{
                type: Output
            }], onButtonClick: [{
                type: Output
            }], sizeSmall: [{
                type: HostBinding,
                args: ['class.bc-inline-alert-small']
            }], sizeLarge: [{
                type: HostBinding,
                args: ['class.bc-inline-alert-large']
            }] } });

class BcInlineAlertModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertModule, declarations: [BcInlineAlertComponent], imports: [BcLinkModule, BcIconButtonModule, BcTransactionStatusModule, CommonModule, RouterLink], exports: [BcInlineAlertComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertModule, imports: [BcLinkModule, BcIconButtonModule, BcTransactionStatusModule, CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInlineAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BcInlineAlertComponent],
                    imports: [BcLinkModule, BcIconButtonModule, BcTransactionStatusModule, CommonModule, RouterLink],
                    exports: [BcInlineAlertComponent]
                }]
        }] });

const InlineAlertTypes = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
    neutral: 'neutral',
    'eventual-full-contrast': 'eventual-full-contrast',
    'eventual-min-contrast': 'eventual-min-contrast'
};
const InlineVersions = {
    Botones: 'Botones',
    Links: 'Links'
};
;

/**
 * Generated bundle index. Do not edit.
 */

export { BcInlineAlertComponent, BcInlineAlertModule, InlineAlertTypes };
